import * as TabsPrimitive from '@radix-ui/react-tabs';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface MobileTab {
  value: string;
  label: ReactNode;
  icon: ReactNode;
  /** A count on the icon (e.g. open work). */
  badge?: number;
  /** A dot on the icon (e.g. a training runs, something is missing). */
  dot?: 'sky' | 'amber';
}

/**
 * A page's tabs on a phone: a bar at the bottom, in thumb reach, like a native app's tab bar — the page's own sections,
 * not the app's navigation (that is in the menu). Inside the page's `Tabs`; its top `TabsList` is hidden on phones. Put
 * `MobileTabsSpacer` at the end of the page, so its end stays above the bar and the home indicator.
 */
export function MobileTabs({ tabs, label }: { tabs: MobileTab[]; label: string }) {
  return (
    <>
      <TabsPrimitive.List
        aria-label={label}
        className="fixed inset-x-0 bottom-0 z-40 flex border-t bg-background/85 pb-[env(safe-area-inset-bottom)] backdrop-blur-md supports-[backdrop-filter]:bg-background/70 md:hidden"
      >
        {tabs.map((tab) => (
          <TabsPrimitive.Trigger
            key={tab.value}
            value={tab.value}
            className={cn(
              'flex h-[3.75rem] min-w-0 flex-1 cursor-pointer flex-col items-center justify-center gap-1 text-[10.5px] font-medium text-muted-foreground outline-none transition-colors active:scale-95 [&_svg]:size-[22px]',
              'data-[state=active]:text-primary data-[state=active]:[&_svg]:stroke-[2.25]',
            )}
          >
            <span className="relative">
              {tab.icon}
              {!!tab.badge && (
                <span className="absolute -top-1.5 -right-2.5 min-w-[18px] rounded-full bg-primary px-1 text-center text-[10px] leading-[18px] font-semibold text-primary-foreground tabular-nums ring-2 ring-background">
                  {tab.badge > 99 ? '99+' : tab.badge}
                </span>
              )}
              {tab.dot && !tab.badge && (
                <span className={cn('absolute -top-0.5 -right-1 size-2 rounded-full ring-2 ring-background', tab.dot === 'sky' ? 'bg-sky-500' : 'bg-amber-500')} />
              )}
            </span>
            <span className="max-w-full truncate px-0.5">{tab.label}</span>
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
    </>
  );
}

/** Room for `MobileTabs` at the end of a page on a phone. */
export function MobileTabsSpacer() {
  return <div aria-hidden className="h-[calc(3.75rem+env(safe-area-inset-bottom))] md:hidden" />;
}
